import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { TenantStore } from "../state/tenant.store";
import { TenantService } from "./tenant.service";
import { TokenStorageService } from "./token-storage.service";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { BackendService } from "./backend-api.service";

/**
 * Logs out the user and redirects to the hostname mismatch error page
 */
async function logoutAndRedirectToError(
  envConfig: EnvironmentLoaderService,
  tokenStorage: TokenStorageService,
  backendService: BackendService,
  router: Router
): Promise<void> {
  try {
    // Clear tokens
    const env = envConfig.getEnvConfig();
    const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;
    tokenStorage.deleteToken(tokenKey);

    // Clear SSO session info
    sessionStorage.removeItem("SSO_LOGIN");

    // Try to logout from backend (don't wait for it)
    backendService.logout().catch(() => {
      // Ignore logout errors
    });

    // Redirect to hostname mismatch error page
    await router.navigate(["/page-error"]);
  } catch (error) {
    // Force redirect even if logout fails
    await router.navigate(["/page-error"]);
  }
}

/**
 * Guard that validates the tenant hostname against the current page hostname.
 * If they don't match, the user is logged out and redirected to an error page.
 */
export async function TenantHostnameGuardFn(): Promise<boolean> {
  const tenantStore = inject(TenantStore);
  const tenantService = inject(TenantService);
  const tokenStorage = inject(TokenStorageService);
  const envConfig = inject(EnvironmentLoaderService);
  const backendService = inject(BackendService);
  const router = inject(Router);

  try {
    if (window.location.hostname === "localhost") return true;

    // Get current tenant info
    let currentTenant = tenantStore.currentTenant;

    // If tenant is not loaded yet, try to load it
    if (!currentTenant) {
      await tenantService.loadTenantInfo();
      currentTenant = tenantStore.currentTenant;
    }

    // If still no tenant info, allow navigation (let other guards handle this)
    if (!currentTenant?.url) {
      return true;
    }

    // Get current page hostname
    const currentHostname = window.location.hostname;

    // Extract hostname from tenant URL
    let tenantHostname: string;
    try {
      const tenantUrl = new URL(currentTenant.url);
      tenantHostname = tenantUrl.hostname;
    } catch (error) {
      // If tenant URL is invalid, treat as relative path or just hostname
      tenantHostname = currentTenant.url.replace(/^https?:\/\//, "").split("/")[0];
    }

    // Check if hostnames match
    if (currentHostname !== tenantHostname) {
      // Hostnames don't match - log out user and redirect to error page
      await logoutAndRedirectToError(envConfig, tokenStorage, backendService, router);
      return false;
    }

    return true;
  } catch (error) {
    // On error, allow navigation to avoid blocking the app
    return true;
  }
}